/**
 * DANAmark — Admin → General Assembly & Live Raffle Draw CMS editor.
 *
 * Scope-limited editor for the single `kind=event / slug=general-assembly-
 * raffle-draw-2026` content document. Reuses the generic `/api/content/{id}`
 * endpoints (same auth, validation and storage as every other event) plus
 * the existing media uploader (ImagePicker).
 *
 * All fields map 1:1 to keys inside `data` on the event record. Blank values
 * are preserved (never auto-filled) so the public page shows only what the
 * editor explicitly approves.
 */
import React, { useEffect, useState } from "react";
import ImagePicker from "./ImagePicker";
import resolveImageUrl from "@/lib/imageUrl";

const SLUG = "general-assembly-raffle-draw-2026";
const PUBLIC_URL = "/events/general-assembly-raffle-draw-2026";
const LANGS = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "es", label: "Español" },
];
const STATUSES = ["draft", "upcoming", "past", "cancelled", "hidden"];
const PAGE_STATES = ["draft", "ready", "live"];
const TZS = ["Europe/Copenhagen", "UTC"];

const emptyI18n = () => ({ en: "", da: "", es: "" });

export default function GeneralAssemblyAdmin({ authed }) {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [picker, setPicker] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const r = await authed.get(`/content?kind=event&status=published`);
      const row = (r.data || []).find(e => ((e.data || {}).slug) === SLUG);
      if (!row) {
        setErr(`Event "${SLUG}" is not seeded in the database.`);
        return;
      }
      setDoc(row);
      setDraft(normalise(row.data || {}));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msgStr = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(" · ")
        : (typeof detail === "string" ? detail : String(e?.message || e));
      setErr(msgStr);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!doc || !draft) return;
    setSaving(true); setErr(""); setMsg("");
    try {
      // `PUT /content/{id}` expects the full ContentIn shape — not a partial
      // patch. Preserve the existing `kind` and `status` so the event stays
      // discoverable by every endpoint that currently queries it.
      await authed.put(`/content/${doc.id}`, {
        kind: doc.kind,
        status: doc.status || "published",
        data: draft,
      });
      await load();
      setMsg("Saved — public page updated.");
      setTimeout(() => setMsg(""), 3500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Pydantic validation errors arrive as an array of objects — stringify
      // them so React can safely render the message.
      const msgStr = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join(" · ")
        : (typeof detail === "string" ? detail : String(e?.message || e));
      setErr(msgStr);
    } finally {
      setSaving(false);
    }
  };

  if (err && !doc) {
    return (
      <div className="border border-line rounded-xl p-8 bg-white text-center text-ink/70" data-testid="ga-admin-error">
        {err}
      </div>
    );
  }
  if (!draft) return <div className="text-ink/50 text-sm" data-testid="ga-admin-loading">Loading…</div>;

  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));
  const setI18n = (key, lang, value) => setDraft(prev => ({
    ...prev, [key]: { ...(prev[key] || emptyI18n()), [lang]: value },
  }));
  const setNested = (parent, child, value) => setDraft(prev => ({
    ...prev, [parent]: { ...(prev[parent] || {}), [child]: value },
  }));

  return (
    <div className="space-y-6" data-testid="ga-admin">
      {/* Header */}
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-mono text-terracotta text-[10px]">EVENTS · COMMUNITY MEETING & LIVE RAFFLE DRAW</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">
              {draft.title?.en || "Community Meeting & Live Raffle Draw"}
            </h2>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <StatusBadge status={draft.status} />
              <PageStateBadge state={draft.page_state} />
              <a href={PUBLIC_URL} target="_blank" rel="noreferrer"
                 data-testid="ga-admin-open-public"
                 className="text-[11px] underline text-ink/70 hover:text-ink">
                Open public page ↗
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {msg && <span className="label-mono text-emerald-700 text-[10px]">{msg}</span>}
            {err && <span className="label-mono text-red-600 text-[10px]">{err}</span>}
            <button onClick={save} disabled={saving} data-testid="ga-admin-save"
                    className="px-5 py-2.5 bg-terracotta text-white text-[11px] uppercase tracking-widest disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </section>

      {/* Core — multilingual title + SEO */}
      <Fieldset title="Title (multilingual)">
        {LANGS.map(l => (
          <Row key={l.code} label={l.label}>
            <input value={draft.title?.[l.code] || ""}
                   onChange={e => setI18n("title", l.code, e.target.value)}
                   data-testid={`ga-admin-title-${l.code}`}
                   className="w-full border border-line rounded px-3 py-2 text-sm" />
          </Row>
        ))}
      </Fieldset>

      {/* Date + times */}
      <Fieldset title="Date & time">
        <Row label="Date (YYYY-MM-DD)">
          <input value={draft.date_iso || ""} onChange={e => set({ date_iso: e.target.value })}
                 placeholder="2026-10-25" data-testid="ga-admin-date"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="Community Meeting start time (HH:MM)">
          <input value={draft.start_time || ""} onChange={e => set({ start_time: e.target.value })}
                 placeholder="13:00" data-testid="ga-admin-start-time"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="Live raffle draw time (HH:MM)">
          <input value={draft.raffle_draw_time || ""} onChange={e => set({ raffle_draw_time: e.target.value })}
                 placeholder="17:00" data-testid="ga-admin-draw-time"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="End time (HH:MM) — optional">
          <input value={draft.end_time || ""} onChange={e => set({ end_time: e.target.value })}
                 placeholder="(blank)" data-testid="ga-admin-end-time"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="Time zone">
          <select value={draft.tz || "Europe/Copenhagen"} onChange={e => set({ tz: e.target.value })}
                  data-testid="ga-admin-tz"
                  className="w-full border border-line rounded px-3 py-2 text-sm">
            {TZS.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </Row>
      </Fieldset>

      {/* Location */}
      <Fieldset title="Location">
        <Row label="Venue (public)">
          <input value={draft.venue || ""} onChange={e => set({ venue: e.target.value })}
                 data-testid="ga-admin-venue"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="City">
          <input value={draft.city || ""} onChange={e => set({ city: e.target.value })}
                 data-testid="ga-admin-city"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="Street address">
          <input value={draft.address || ""} onChange={e => set({ address: e.target.value })}
                 data-testid="ga-admin-address"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="Show address publicly">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!draft.address_visible}
                   onChange={e => set({ address_visible: e.target.checked })}
                   data-testid="ga-admin-address-visible" />
            <span>Address appears on the public page</span>
          </label>
          <div className="mt-2 border-l-2 border-red-500 bg-red-50 text-red-800 text-[12px] px-3 py-2 rounded-r"
               data-testid="ga-admin-address-warning">
            <strong>Privacy warning.</strong> Enabling this exposes the private
            event address to anyone on the public internet (visitors, crawlers,
            search engines). Only turn it on if the venue is intended to be
            publicly listed. The address is also stripped from the public API
            response while this is off.
          </div>
        </Row>
      </Fieldset>

      {/* Description (multilingual) */}
      <Fieldset title="Description (multilingual)">
        {LANGS.map(l => (
          <Row key={l.code} label={l.label}>
            <textarea rows={5} value={draft.description?.[l.code] || ""}
                      onChange={e => setI18n("description", l.code, e.target.value)}
                      data-testid={`ga-admin-description-${l.code}`}
                      className="w-full border border-line rounded px-3 py-2 text-sm font-mono" />
          </Row>
        ))}
      </Fieldset>

      {/* Livestream / CTA */}
      <Fieldset title="Livestream & CTA">
        <Row label="Livestream URL (external)">
          <input value={draft.livestream_url || ""} onChange={e => set({ livestream_url: e.target.value })}
                 placeholder="https://…" data-testid="ga-admin-livestream"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
        <Row label="CTA label (multilingual)">
          <div className="grid grid-cols-3 gap-2">
            {LANGS.map(l => (
              <input key={l.code} value={draft.cta_label?.[l.code] || ""}
                     onChange={e => setI18n("cta_label", l.code, e.target.value)}
                     placeholder={l.label} data-testid={`ga-admin-cta-label-${l.code}`}
                     className="border border-line rounded px-3 py-2 text-sm" />
            ))}
          </div>
        </Row>
        <Row label="CTA URL">
          <input value={draft.cta_url || ""} onChange={e => set({ cta_url: e.target.value })}
                 placeholder="/join or https://…" data-testid="ga-admin-cta-url"
                 className="w-full border border-line rounded px-3 py-2 text-sm" />
        </Row>
      </Fieldset>

      {/* Cover image */}
      <Fieldset title="Cover image">
        <div className="flex items-start gap-4">
          <div className="w-48 h-32 border border-line rounded overflow-hidden bg-line/10 shrink-0">
            {draft.cover_image ? (
              <img src={resolveImageUrl(draft.cover_image)} alt=""
                   data-testid="ga-admin-cover-preview"
                   className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink/40 text-xs">No image</div>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex gap-2">
              <button onClick={() => setPicker(true)} data-testid="ga-admin-cover-pick"
                      className="px-3 py-2 text-[11px] uppercase tracking-widest border border-ink hover:bg-ink hover:text-white">
                {draft.cover_image ? "Replace image" : "Choose image"}
              </button>
              {draft.cover_image && (
                <button onClick={() => set({ cover_image: "" })} data-testid="ga-admin-cover-clear"
                        className="px-3 py-2 text-[11px] uppercase tracking-widest border border-line text-ink/70 hover:text-ink">
                  Remove
                </button>
              )}
            </div>
            <div className="text-xs text-ink/60">
              Image stays until a replacement is saved successfully.
            </div>
          </div>
        </div>
      </Fieldset>

      {/* Publication */}
      <Fieldset title="Publication">
        <Row label="Status">
          <select value={draft.status || "draft"} onChange={e => set({ status: e.target.value })}
                  data-testid="ga-admin-status"
                  className="w-full border border-line rounded px-3 py-2 text-sm">
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="text-xs text-ink/60 mt-1">
            Public states: <code>upcoming</code>, <code>past</code>. Draft/cancelled/hidden hide the page.
          </div>
        </Row>
        <Row label="Page state (editorial)">
          <select value={draft.page_state || "draft"} onChange={e => set({ page_state: e.target.value })}
                  data-testid="ga-admin-page-state"
                  className="w-full border border-line rounded px-3 py-2 text-sm">
            {PAGE_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Row>
      </Fieldset>

      {/* SEO */}
      <Fieldset title="SEO">
        <Row label="SEO title (multilingual)">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {LANGS.map(l => (
              <input key={l.code} value={draft.seo?.title?.[l.code] || ""}
                     onChange={e => setNested("seo", "title", { ...(draft.seo?.title || emptyI18n()), [l.code]: e.target.value })}
                     placeholder={l.label} data-testid={`ga-admin-seo-title-${l.code}`}
                     className="border border-line rounded px-3 py-2 text-sm" />
            ))}
          </div>
        </Row>
        <Row label="SEO description (multilingual)">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {LANGS.map(l => (
              <textarea key={l.code} rows={3} value={draft.seo?.description?.[l.code] || ""}
                        onChange={e => setNested("seo", "description", { ...(draft.seo?.description || emptyI18n()), [l.code]: e.target.value })}
                        placeholder={l.label} data-testid={`ga-admin-seo-desc-${l.code}`}
                        className="border border-line rounded px-3 py-2 text-sm" />
            ))}
          </div>
        </Row>
      </Fieldset>

      {picker && (
        <ImagePicker authed={authed}
                     onPick={(asset) => { set({ cover_image: asset.url }); setPicker(false); }}
                     onClose={() => setPicker(false)} />
      )}
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────
function normalise(d) {
  return {
    slug: d.slug || SLUG,
    title: { ...emptyI18n(), ...(d.title || {}) },
    date_iso: d.date_iso || "",
    start_time: d.start_time || "",
    end_time: d.end_time || "",
    tz: d.tz || "Europe/Copenhagen",
    raffle_draw_time: d.raffle_draw_time || "",
    venue: d.venue || "",
    city: d.city || "",
    address: d.address || "",
    address_visible: !!d.address_visible,
    description: { ...emptyI18n(), ...(d.description || {}) },
    livestream_url: d.livestream_url || "",
    cta_label: { ...emptyI18n(), ...(d.cta_label || {}) },
    cta_url: d.cta_url || "",
    cover_image: d.cover_image || "",
    status: d.status || "upcoming",
    page_state: d.page_state || "live",
    seo: {
      title: { ...emptyI18n(), ...((d.seo || {}).title || {}) },
      description: { ...emptyI18n(), ...((d.seo || {}).description || {}) },
      image: (d.seo || {}).image || "",
      canonical_url: (d.seo || {}).canonical_url || PUBLIC_URL,
    },
  };
}

function Fieldset({ title, children }) {
  return (
    <section className="border border-line rounded-xl p-6 bg-white">
      <div className="label-mono text-ink/60 text-[10px] tracking-widest mb-4">{title.toUpperCase()}</div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-3 md:gap-6 items-start">
      <label className="text-sm text-ink/80 pt-2">{label}</label>
      <div>{children}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    draft: "bg-line/30 text-ink/70",
    upcoming: "bg-emerald-100 text-emerald-800",
    past: "bg-sky-100 text-sky-800",
    cancelled: "bg-red-100 text-red-800",
    hidden: "bg-stone-200 text-stone-600",
  };
  const c = colors[status] || colors.draft;
  return <span data-testid="ga-admin-status-badge" className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${c}`}>{(status || "draft").toUpperCase()}</span>;
}

function PageStateBadge({ state }) {
  const colors = {
    draft: "bg-line/30 text-ink/70",
    ready: "bg-amber-100 text-amber-800",
    live: "bg-emerald-100 text-emerald-800",
  };
  const c = colors[state] || colors.draft;
  return <span data-testid="ga-admin-page-state-badge" className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${c}`}>PAGE · {(state || "draft").toUpperCase()}</span>;
}
